import { useState } from 'react';

import { useIsFetching, useQueryClient } from '@tanstack/react-query';

import { Button } from '@components/button';
import { createForm } from '@components/form';
import { IconEmail } from '@components/icons/email';
import { Separator } from '@components/separator';
import { Sidebar, hideSidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { useEditEmailSettings } from 'api/company/hooks';
import { EmailSettings } from 'api/company/requests';

export const useManagedEmailDomainFormSidebar = () =>
  useSidebar(EmailDomainForm, { variant: 'REGULAR' });

const Form = createForm<EmailSettings>();

const EmailDomainForm = ({
  domain,
  fromAddress,
  fromName,
}: {
  domain?: string;
  fromAddress?: string;
  fromName?: string;
}) => {
  const { t } = useScopedTranslate(locale);
  const submitMutation = useEditEmailSettings();
  const queryClient = useQueryClient();
  const isFetching = useIsFetching({ queryKey: ['company-email-settings'] }) > 0;
  const isEditing = Boolean(domain);

  const [errors, setErrors] = useState<
    Partial<Record<'domain' | 'fromAddress' | 'fromName', string>>
  >({});

  const submit = async (values: EmailSettings) => {
    const fromDomain = values.fromAddress.split('@')[1];
    if (fromDomain !== values.domain) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        fromAddress: t('emailSettingsFromAddressError'),
      }));
    } else {
      await submitMutation.mutateAsync(values);
      await queryClient.refetchQueries(['company-email-settings']);
      hideSidebar();
    }
  };

  return (
    <Sidebar>
      <Sidebar.SimpleHeader
        icon={IconEmail}
        iconColor="elementBlue100"
        title={isEditing ? t('editEmailDomain') : t('addEmailDomain')}
      />
      <Sidebar.Content>
        <Form onSubmit={submit} overrideErrors={errors}>
          <Stack space={6}>
            <Text color="textSecondary" font="regular14" text={t('description')} />
            <Text color="textSecondary" font="regular14" text={t('descriptionNote')} />
            <Stack space={4}>
              <Form.TextField
                id="domain"
                label={t('emailSettingsDomain')}
                validators={['required']}
                placeholder={t('emailDomainPlaceholder')}
                defaultValue={domain}
              />
              <Separator />
              <Form.TextField
                id="fromAddress"
                label={t('emailSettingsFromAddress')}
                validators={['required']}
                placeholder={t('fromAddressPlaceholder')}
                defaultValue={fromAddress}
              />
              <Form.TextField
                id="fromName"
                label={t('emailSettingsFromName')}
                validators={['required', 'email-sender-name']}
                placeholder={t('fromNamePlaceholder')}
                defaultValue={fromName}
              />
            </Stack>
          </Stack>
          <Sidebar.Footer>
            <Form.Actions>
              <Button
                text={t('core.cancel')}
                isDisabled={submitMutation.isLoading || isFetching}
                onClick={hideSidebar}
                variant="secondary"
              />
              <Form.Submit
                text={domain ? t('core.save') : t('addDomain')}
                variant="primary-blue"
                isDisabled={submitMutation.isLoading || isFetching}
              />
            </Form.Actions>
          </Sidebar.Footer>
        </Form>
      </Sidebar.Content>
    </Sidebar>
  );
};

const locale = {
  en: {
    addEmailDomain: 'Add email domain (DNS)',
    editEmailDomain: 'Edit email domain (DNS)',
    addDomain: 'Add domain',
    emailSettings: 'Email settings',
    emailSettingsDescription: 'Configure your email settings',
    emailSettingsToggle: 'Enable custom email settings',
    emailSettingsDomain: 'Domain',
    emailSettingsFromAddress: 'Send from address',
    emailSettingsFromName: 'Send from name',
    emailSettingsFromAddressError: 'From address must be from the same domain',
    emailDomainPlaceholder: 'acme.com',
    fromAddressPlaceholder: 'E.g. journeys@acme.com',
    fromNamePlaceholder: 'Acme Inc.',
    description: 'Enter the address of your domain you want to send emails from',
    descriptionNote:
      'Please note: you must have access to your email server DNS Records (txt) to be able to complete the next step',
  },
};
